import React, {Component, Fragment} from 'react';
import {connect} from "react-redux";
import H from "history";
import Table, {TableColumn} from '../../Table';
import ApiService from '../../ApiService';
import DispatchProps from '../../../utils/DispatchProps';
import LoadingCard from '../../LoadingCard';
import AnalyticsDateControls from '../../AnalyticsDateControls';
import {IRootState} from '../../../store';
import {TableRowProps} from '../../TableRow';
import {cellForColumn} from './SubmissionAssignmentAnalyticsUtils';
import {YearGroupSubmissionSummary} from '../../../models/analytics/AssignmentSubmissionSummaries';
import AcademicYearPicker from '../../AcademicYearPicker';
import {openFileDownload} from '../../../utils/Utils';
import SchoolMenu from '../../SchoolMenu';
import {CommandBarButton} from 'office-ui-fabric-react/lib-commonjs';
import {AnalyticsDoughnutPalette} from "../../assignment-analytics/AnalyticsDoughnutUtils";
import { Bar } from "react-chartjs-2";
import SchoolPageTitle from '../../SchoolPageTitle';
import AnalyticsControlBar from "../../assignment-analytics/AnalyticsControlBar";

interface SchoolYearGroupSubmissonAnalyticsPageProps {
    tenantId: string;
    schoolCode: string
    history: H.History;
    apiService: ApiService;
    startDate?: string,
    endDate?: string,
    academicYear: number,
}

interface SchoolYearGroupSubmissonAnalyticsPageState {
    results?: YearGroupSubmissionSummary[];
}

export const BAR_CHART_OPTIONS = {
    legend: {
        display: true,
        position: "top"
    },
    title: {
        text: "Student submissions by year group",
        display: true,
        position: "bottom",
        padding: 0,
        fontStyle: "light",
        fontColor: "rgba(0,0,0,0.8)"
    },
    tooltips: {
        callbacks: {
            title: function(tooltipItem: any,data: any) {
                return tooltipItem[0].label;
            }
        }
    },
    scales: {
        yAxes: [{
            stacked: true,
            gridLines: {
                display: true,
                drawOnChartArea: true,
                drawBorder: false
            },
            drawTicks: true,
            ticks: {
                beginAtZero: true,
                precision: 0,
            }
        }],
        xAxes: [{
            stacked: true,
            gridLines: {
                drawOnChartArea: false,
            },
            ticks: {
            },
        }]
    }
}


class SchoolYearGroupSubmissonAnalyticsPage extends Component<SchoolYearGroupSubmissonAnalyticsPageProps & DispatchProps, SchoolYearGroupSubmissonAnalyticsPageState> {
    static displayName = SchoolYearGroupSubmissonAnalyticsPage.name;

    constructor(props: SchoolYearGroupSubmissonAnalyticsPageProps & DispatchProps) {
        super(props);
        this.state = {}
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear) {
            await this.getResults();
        }
    }

    async componentDidUpdate(prevProps: Readonly<SchoolYearGroupSubmissonAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear) {
            await this.getResults();
        }
    }

    private async getResults(): Promise<void> {
        this.setState({
            results: undefined
        });

        const results = await this.props.apiService.getYearGroupSubmissionsAnalyticsForSchool(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!);

        this.setState({
            results
        });
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private async export(): Promise<void> {
        var result = await this.props.apiService.getYearGroupSubmissionsAnalyticsForSchoolExport(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!)
        openFileDownload(result);
    }

    private static readonly columns: TableColumn[] = [
        { title: "Year", propertyPath: "yearName", enableSort: true, flex: 4 },
        { title: "Assignments Received", propertyPath: "submissionsTotal", extraDetail: "Total assignments the students in this year have received", enableSort: true, flex: 2 },
        { title: "Not Completed", propertyPath: "submissionsNotCompletedPercentage", extraDetail: "Not submitted within 2 weeks of due date", enableSort: true, flex: 2 },
        { title: "Late", propertyPath: "submissionsSubmittedAfterDueDatePercentage", extraDetail: "Submitted within 2 weeks after the due date", enableSort: true, flex: 2 },
        { title: "On Time", propertyPath: "submissionsSubmittedBeforeDueDatePercentage", extraDetail: "Submitted before the due date", enableSort: true, flex: 2 },
    ];

    private readonly rowProps = (item: YearGroupSubmissionSummary): TableRowProps => {
        return {
            enableRowLink: false
        };
    }

    private buildCharts = (): JSX.Element | undefined => {
        
        const data = {
            labels: this.state.results?.map(r => r.yearName),
            datasets: [
                {
                    label: "On time",
                    backgroundColor: AnalyticsDoughnutPalette.green,
                    data: this.state.results?.map(r => r.submissionsSubmittedBeforeDueDateCount)
                },
                {
                    label: "Late",
                    backgroundColor: AnalyticsDoughnutPalette.amber,
                    data: this.state.results?.map(r => r.submissionsSubmittedAfterDueDateCount)
                },
                {
                    label: "Not Completed",
                    backgroundColor: AnalyticsDoughnutPalette.grey,
                    data: this.state.results?.map(r => r.submissionsNotCompletedCount)
                }
            ]
        };
        
        let withSubmissionsCount: number = this.state.results!.filter(r => r.submissionsTotal > 0).length;
        let withSubmissionsPercentage = this.state.results?.length == 0 ? 0 : ((withSubmissionsCount / this.state.results!.length) * 100).toFixed(0);

        return (
            <div className={"card flex-row flex--justify-content-space-around p-05rem"} style={{marginTop: "0.25rem"}}>
                <div className={"number-stat flex-item flex-item--flex-1"}>
                    <div className="number-stat__number number-stat__number--small">
                        {withSubmissionsPercentage}%
                    </div>
                    <div className={"number-stat__description"}>Year groups with assignments</div>
                </div>
                
                <div className={"flex-item flex-item--flex-2 p-05rem"}>
                    <Bar data={data} options={BAR_CHART_OPTIONS} height={2} width={9}/>
                </div>
            </div>
        );
    }

    private buildResults(): JSX.Element {
        if (this.state.results === undefined) {
            return <LoadingCard />;
        }
        else {
            return <Fragment>
                
                {this.buildCharts()}
                <Table
                    title={"Years"}
                    items={this.state.results}
                    columns={SchoolYearGroupSubmissonAnalyticsPage.columns}
                    cellForColumn={cellForColumn}
                    rowPropsForItem={this.rowProps}
                    defaultSortColumnPath={'yearName'}
                    enableSort={true}>

                    <CommandBarButton
                        iconProps={{ iconName: "DownloadDocument" }}
                        text="Export All"
                        onClick={() => this.export()}/>
                </Table>
            </Fragment >
        }
    }

    render() {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Assignment Analytics - Year Groups" reverse={true} />
                <SchoolMenu prefix="Assignment Analytics" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/assignment-analytics-overview?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`} suffix="Years" />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <AnalyticsControlBar tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} current={"yearGroup"} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} onAcademicYearChanged={this.onAcademicYearChanged} history={this.props.history}/>
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): SchoolYearGroupSubmissonAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear
        };
    }
)(SchoolYearGroupSubmissonAnalyticsPage)